'use client';

import { Fragment } from 'react';
import { BatteryRings } from './BatteryRings';
import { WeightRipples } from './WeightRipples';
import type { SpotlightPanel as SpotlightPanelData } from './types';
import styles from './SpotlightPanel.module.css';

type SpotlightPanelProps = {
  panel: SpotlightPanelData;
  theme: 'dark' | 'light';
};

export const SpotlightPanel = ({ panel, theme }: SpotlightPanelProps) => {
  const panelClass = `${styles.panel} ${
    theme === 'dark' ? styles.panelDark : styles.panelLight
  }`;

  if (panel.type === 'intro') {
    return (
      <article className={panelClass} data-ff-card>
        <div className={`${styles.clip} ${styles.clipMatchMedia}`}>
          <p className={styles.introBody} data-ff-card-copy>
            {panel.body}
          </p>
        </div>
      </article>
    );
  }

  if (panel.type === 'metric') {
    const isWeight = panel.variant === 'weight';

    return (
      <article className={panelClass} data-ff-card>
        <div
          className={`${styles.clip} ${styles.clipFixedHeight} ${
            isWeight ? '' : styles.clipCompact
          }`}
        >
          <div className={styles.metricDecor} aria-hidden="true">
            {isWeight ? <WeightRipples /> : <BatteryRings />}
          </div>
          <div
            className={`${styles.metricLayout} ${
              isWeight ? '' : styles.metricAlignTop
            }`}
          >
            {panel.eyebrow ? (
              <p className={styles.metricEyebrow} data-ff-card-copy>
                {panel.eyebrow}
              </p>
            ) : null}
            <div className={styles.metricValueRow}>
              <p
                className={`${styles.metricValue} ${
                  panel.unit ? styles.metricValueCompact : ''
                }`}
                data-ff-card-copy
              >
                {panel.value}
              </p>
              {panel.unit ? (
                <p
                  className={`${styles.metricValue} ${styles.metricValueCompact}`}
                  data-ff-card-copy
                >
                  {panel.unit}
                </p>
              ) : null}
            </div>
            <p className={styles.metricCaption} data-ff-card-copy>
              {panel.caption}
            </p>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className={panelClass} data-ff-card>
      <div className={styles.clip}>
        <div className={styles.featuresLayout}>
          <p className={styles.featuresEyebrow} data-ff-card-copy>
            {panel.eyebrow}
          </p>
          <ul className={styles.featureList}>
            {panel.items.map((item, index) => (
              <Fragment key={item.title}>
                {index > 0 ? (
                  <li className={styles.divider} aria-hidden="true" />
                ) : null}
                <li className={styles.featureItem}>
                  <span className={styles.featureIcon} aria-hidden="true">
                    <img src={item.icon} alt="" width={24} height={24} />
                  </span>
                  <div className={styles.featureCopy}>
                    <p className={styles.featureTitle} data-ff-card-copy>
                      {item.title}
                    </p>
                    <p className={styles.featureBody} data-ff-card-copy>
                      {item.body}
                    </p>
                  </div>
                </li>
              </Fragment>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
};
